import {
  Card,
  Button,
  Container,
  Col,
  Row,
} from "react-bootstrap";
import { useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SETTINGS, DASHBOARD, USER } from "../../services/actions";
import { Warning, Tag } from "@phosphor-icons/react";
import { useNavigate, useLocation } from "react-router-dom";
import React from "react";
import StackBar from "./StackBar";
import { Knob } from "primereact/knob";
import { DataScroller } from "primereact/datascroller";
import { Menubar } from "primereact/menubar";
import { Toast } from "primereact/toast";


export default function Dashboard() {
  const documentStyle = getComputedStyle(document.documentElement);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const toastRef = useRef(null);


  // Redux selectors
  const dashboardState = useSelector((state) => state?.dashboardState);
  const usersState = useSelector((state) => state?.userState);


  // ✅ REMOVED: Aggressive back button prevention
  // ✅ REPLACED WITH: Proper navigation guard with confirmation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Only show warning if there's unsaved work
      const hasUnsavedChanges = false; // Set this based on your actual state
      
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };


    // ✅ BETTER APPROACH: Warn on tab close, not block back button
    window.addEventListener('beforeunload', handleBeforeUnload);


    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);


  // ✅ ALTERNATIVE: Custom back navigation handler (optional)
  useEffect(() => {
    let isNavigatingAway = false;


    const handlePopState = (event) => {
      if (isNavigatingAway) return;


      // Show confirmation dialog
      const userConfirmed = window.confirm(
        'Are you sure you want to leave the dashboard? This will end your session.'
      );


      if (userConfirmed) {
        isNavigatingAway = true;
        // Clear session data
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('customerData');
        // Allow navigation
        navigate('/', { replace: true });
      } else {
        // User cancelled, restore forward history
        window.history.pushState(null, '', location.pathname);
      }
    };


    // Push initial state
    window.history.pushState(null, '', location.pathname);
    window.addEventListener('popstate', handlePopState);


    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate, location.pathname]);


  // ✅ OPTIMIZED: Fetch dashboard data once on mount
  useEffect(() => {
    console.log('📊 [Dashboard] Fetching dashboard data');
    
    try {
      dispatch({ type: `${SETTINGS}_GET`, payload: 1 });
      dispatch({ type: `${DASHBOARD}_GET`, payload: 0 });
      dispatch({ type: `${USER}_GET`, payload: 0 });
    } catch (err) {
      console.error('❌ [Dashboard] Error fetching data:', err);
    }
  }, [dispatch]); // ✅ Added dispatch to dependencies


  // ✅ REMOVED: Unnecessary logging useEffects
  // Use React DevTools or browser console to inspect state instead


  // ✅ OPTIMIZED: Memoized color mapping
  const colors = useMemo(() => [
    "--blue-500",
    "--green-500", 
    "--yellow-500",
    "--red-500"
  ], []);


  const getColor = useCallback((index) => {
    const colorVar = colors[index % colors.length];
    return documentStyle.getPropertyValue(colorVar);
  }, [colors, documentStyle]);


  // ✅ OPTIMIZED: Memoized item template
  const itemTemplate = useCallback((data) => {
    return (
      <div className="col-12">
        <div className="flex flex-column xl:flex-row xl:align-items-start p-4 gap-4">
          <img
            className="w-9 sm:w-16rem xl:w-10rem shadow-2 block xl:block mx-auto border-round"
            width={60}
            src={data.avatar}
            alt={data.first_name}
            loading="lazy" // ✅ Lazy load images
          />
          <div className="flex flex-column lg:flex-row justify-content-between align-items-center xl:align-items-start lg:flex-1 gap-4">
            <div className="flex flex-column align-items-center lg:align-items-start gap-3">
              <div className="flex flex-column gap-1">
                <div className="text-2xl font-bold text-900">
                  {data.first_name}
                </div>
                <div className="text-700">{data.last_name}</div>
              </div>
              <div className="flex flex-column gap-2">
                <span className="flex align-items-center gap-2">
                  <i className="pi pi-tag product-category-icon"></i>
                  <span className="font-semibold">{data.main_role}</span>
                </span>
              </div>
            </div>
            <div className="flex flex-row lg:flex-column align-items-center lg:align-items-end gap-4 lg:gap-2">
              <span className="text-2xl font-semibold">${data.price}</span>
              <Button
                icon="pi pi-shopping-cart"
                label="Add to Cart"
                disabled={data.inventoryStatus === "OUTOFSTOCK"}
              />
              <Tag value={data.main_role} />
            </div>
          </div>
        </div>
      </div>
    );
  }, []);


  // ✅ OPTIMIZED: Memoized menu toolbar component
  const MenuToolBar = useMemo(() => {
    const showToast = (severity, summary, detail) => {
      if (toastRef.current) {
        toastRef.current.show({
          severity,
          summary,
          detail,
          life: 3000,
        });
      }
    };


    const items = [
      {
        label: "File",
        icon: "pi pi-file",
        items: [
          {
            label: "New",
            icon: "pi pi-plus",
            command: () => showToast("success", "Success", "File created"),
          },
          {
            label: "Print",
            icon: "pi pi-print",
            command: () => showToast("error", "Error", "No printer connected"),
          },
        ],
      },
      {
        label: "Search",
        icon: "pi pi-search",
        command: () => showToast("warn", "Search Results", "No results found"),
      },
      {
        separator: true,
      },
      {
        label: "Sync",
        icon: "pi pi-cloud",
        items: [
          {
            label: "Import",
            icon: "pi pi-cloud-download",
            command: () => showToast("info", "Downloads", "Downloaded from cloud"),
          },
          {
            label: "Export",
            icon: "pi pi-cloud-upload",
            command: () => showToast("info", "Shared", "Exported to cloud"),
          },
        ],
      },
    ];


    return (
      <div className="card">
        <Menubar model={items} />
        <Toast ref={toastRef} />
      </div>
    );
  }, []);


  // ✅ OPTIMIZED: Memoized dashboard results
  const dashboardResults = useMemo(() => {
    return dashboardState?.data?.data?.results || [];
  }, [dashboardState?.data?.data?.results]);


  const usersResults = useMemo(() => {
    return usersState?.data?.data?.results || [];
  }, [usersState?.data?.data?.results]);


  // ✅ Loading state
  if (dashboardState?.loading || usersState?.loading) {
    return (
      <Container fluid className="py-3 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading dashboard data...</p>
      </Container>
    );
  }


  // ✅ Error state
  if (dashboardState?.error || usersState?.error) {
    return (
      <Container fluid className="py-3">
        <div className="alert alert-danger" role="alert">
          <Warning size={24} className="me-2" />
          Error loading dashboard: {dashboardState?.error || usersState?.error}
        </div>
      </Container>
    );
  }


  return (
    <Container fluid className="py-3">
      <Row>
        {dashboardResults.map((v, i) => {
          const color = getColor(i);
          return (
            <Col key={`dashboard-item-${i}`} className="py-3">
              <Card
                role="region"
                className="p-2"
                style={{
                  border: `solid ${color}`,
                  borderWidth: "0 0 0 6px",
                  color: "#696cff",
                }}
              >
                <Knob
                  value={v.count}
                  min={0}
                  size={50}
                  strokeWidth={16}
                  readOnly
                />
                {v.name}
              </Card>
            </Col>
          );
        })}
      </Row>


      <Row>
        <Col md={4} className="py-3">
          <Card role="region" className="p-2">
            <DataScroller
              value={usersResults}
              itemTemplate={itemTemplate}
              rows={5}
              inline
              scrollHeight="500px"
              header="Users List"
            />
          </Card>
        </Col>


        <Col md={8}>
          <Row>
            <Col className="pt-3" md={12}>
              {MenuToolBar}
            </Col>
            {[1, 2, 3].map((_, index) => (
              <Col key={`stackbar-${index}`} className="py-3" md={6}>
                <StackBar />
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </Container>
  );
}
